import {
    Button,
    Spinner,
    Table, TableBody,
    TableCell,
    TableCellActions,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    makeStyles, shorthands
} from "@fluentui/react-components";
import debounce from 'lodash/debounce';
import { DeleteFilled, DeleteRegular, bundleIcon } from "@fluentui/react-icons";

import moment from 'moment';
import React, { useEffect, useState } from "react";
import { useAuthState } from "react-firebase-hooks/auth";
import { useNavigate } from "react-router-dom";
import "./Event.css";
import CustomSearchInput from "./components/CustomSearchInput";
import Layout from "./components/Layout";
import { auth, db, trackException, userAnalytics } from "./services/firebase";
import { collection, getDocs, query, orderBy, startAfter, limit, updateDoc, doc } from "firebase/firestore";
import { PlusSVG } from "./static/svgs";
import DeleteEventDialog from "./components/DeleteEventDialog";
const DeleteIcon = bundleIcon(DeleteFilled, DeleteRegular);

const useStyles = makeStyles({
    root: {
        ...shorthands.overflow("hidden"),
        display: "flex",
        height: "480px",
        backgroundColor: "#fff"
    },
    content: {
        ...shorthands.flex(1),
        ...shorthands.padding("16px"),
        display: "block",
        ...shorthands.gridArea("main")
    }
});

const columns = [
    { columnKey: "fullName", label: "Guest Name" },
    { columnKey: "email", label: "Email" },
    { columnKey: "phone", label: "Phone" },
    { columnKey: "createdAt", label: "Created At" }
];

function PreRegisteredGuests() {
    const styles = useStyles();
    const navigate = useNavigate();
    const [user, loading] = useAuthState(auth);

    const [allGuests, setAllGuests] = useState([]);
    const [filteredGuests, setFilteredGuests] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [pageSize] = useState(50);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMoreData, setHasMoreData] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = React.useState(false);
    const [selectedGuest, setSelectedGuest] = useState();

    useEffect(() => {
        if (loading) return;
        if (!user) return navigate("/");
        userAnalytics(user);
        loadGuests(true);
    }, [user, loading]);

    useEffect(() => {
        if (page > 1) {
            loadGuests(false);
        }
    }, [page]);

    const loadGuests = async (reset = false) => {
        setIsLoading(true);
        try {
            const collRef = collection(db, "PreRegisteredGuests");
            let q = query(collRef, orderBy("createdAt", "desc"));

            if (!reset && lastVisible) {
                q = query(collRef, orderBy("createdAt", "desc"), startAfter(lastVisible), limit(pageSize));
            } else {
                q = query(collRef, orderBy("createdAt", "desc"), limit(pageSize));
            }

            const snapshot = await getDocs(q);
            const docs = snapshot.docs;
            const newData = docs.map(doc => ({
                id: doc.id,
                firstName: doc.data().firstName,
                lastName: doc.data().lastName,
                email: doc.data().email,
                phone: doc.data().phone,
                createdAt: doc.data().createdAt,
                fullName: `${doc.data().firstName} ${doc.data().lastName}`
            }));

            setLastVisible(docs[docs.length - 1] || null);

            setAllGuests(prev => reset ? newData : [...prev, ...newData]);
            setFilteredGuests(prev => reset ? newData : [...prev, ...newData]);

            if (docs.length < pageSize) setHasMoreData(false);
        } catch (err) {
            console.error(err);
            trackException(err.message || err);
        } finally {
            setIsLoading(false);
        }
    };

    const debouncedSearch = debounce((term) => {
        const lower = term.toLowerCase();
        const filtered = allGuests.filter(guest =>
            guest.fullName.toLowerCase().includes(lower) ||
            guest.email.toLowerCase().includes(lower) ||
            guest.phone.includes(lower)
        );
        setFilteredGuests(filtered);
    }, 300);

    const handleSearch = e => {
        const val = e.target.value;
        setSearchTerm(val);
        debouncedSearch(val);
    };

    const handleClear = () => {
        setSearchTerm('');
        debouncedSearch('');
    };

    const openDeleteForm = (selectedItem) => {
        setSelectedGuest(selectedItem);
        setIsDeleteDialogOpen(true);
    }

    const DeletePost = async () => {
        if (!selectedGuest?.id) return;
        try {
            const guestRef = doc(db, "PreRegisteredGuests", selectedGuest.id);
            await updateDoc(guestRef, {
                isDeleted: true
            });
    
            // Close dialog
            setIsDeleteDialogOpen(false);
    
            // Refetch guests
            setAllGuests(prev => prev.filter(g => g.id !== selectedGuest.id));
            setFilteredGuests(prev => prev.filter(g => g.id !== selectedGuest.id));
        } catch (err) {
            console.error("Error deleting guest:", err);
            trackException(err.message || err);
        }
    };

    return (
        <Layout>
            <div className={styles.content}>
                <div className="d-flex justify-space-between">
                    <Button icon={<PlusSVG />} className="primary-btn" shape="circular" appearance="primary" onClick={() => { /* New guest if needed */ }}>
                        {'NEW GUEST'.toUpperCase()}
                    </Button>
                    <CustomSearchInput
                        value={searchTerm}
                        onChange={handleSearch}
                        onClick={handleClear}
                    />
                </div>

                <div className="table-layout">
                    <Table aria-label="Guest Table">
                        <TableHeader className="list-row">
                            <TableRow>
                                {columns.map(col => (
                                    <TableHeaderCell key={col.columnKey}>
                                        <span className="listHeaderText">{col.label}</span>
                                    </TableHeaderCell>
                                ))}
                            </TableRow>
                        </TableHeader>

                        <TableBody>
                            {filteredGuests.map(guest => (
                                <TableRow key={guest.id}>
                                    <TableCell className="table-row-fonts__primary">
                                        <TableCellLayout>
                                            {guest.fullName}
                                        </TableCellLayout>
                                    </TableCell>
                                    <TableCell className="table-row-fonts__desc">{guest.email}</TableCell>
                                    <TableCell className="table-row-fonts__desc">{guest.phone}</TableCell>
                                    <TableCell className="table-row-fonts__desc">
                                        {guest.createdAt ? moment(guest.createdAt.toDate()).format('Do MMMM YYYY hh:mm A') : '—'}
                                        <TableCellActions>
                                            <Button
                                                icon={<DeleteIcon />}
                                                appearance="subtle"
                                                aria-label="Delte"
                                                onClick={() => openDeleteForm(guest)}
                                            />
                                        </TableCellActions>
                                    </TableCell>
                                    
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <div style={{ marginTop: '10px', textAlign: 'center' }}>
                        {isLoading && <Spinner />}
                        {!isLoading && hasMoreData && searchTerm === "" && (
                            <Button onClick={() => setPage(prev => prev + 1)}>Load More...</Button>
                        )}
                    </div>
                </div>
            </div>
            <DeleteEventDialog isDeleteDialogOpen={isDeleteDialogOpen}
                setIsDeleteDialogOpen={setIsDeleteDialogOpen} DeleteEvent={DeletePost} text={'Guest'} />
        </Layout>
    );
}

export default PreRegisteredGuests;
